
import { ColDef, ColumnState, DateFilterModel, TextFilterModel, SetFilterModel } from 'ag-grid-community';
import { Component, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { selectAllTransactions, selectHasNoRecentTransactions, selectHasNoRecurringTransactions, selectHasNoSavedTemplates, selectHasNoTransactions, selectHasNoUpcomingTransactions, selectHeaderStateServiceError, selectIsDemoUser, selectIsEmployee, selectIsPtrs, selectRecentTransactions, selectRecurringTransactions, selectSubAccountId, selectTemplateDeleteCallSuccessful, selectTemplatesList, selectTemplateStartTransactionStarted, selectUpcomingTransactions } from '../../../../app/account-info-header/reducers';
import { AccountInfoState } from '../../../account-info-header/models/AccountInfoState';
import { SharedActions } from '../../../shared/actions';
import { ActionsLinkComponent } from '../../../shared/actions-link/actions-link.component';
import { displayCancelModal } from '../../../shared/actions/shared.actions';
import { SloaNicknameComponent } from '../../../shared/components/sloa-nickname/sloa-nickname.component';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import { selectCancelModalContinueClickedTransactionsTablePage, selectCancelModalSource, selectCreateTypeInContext, selectPageScrollTo } from '../../../shared/reducers';
import { agFilterNames } from '../../../shared/types/AgFilterNames';
import { selectedRowData, TableComponentState } from '../../../shared/types/TableComponentState';
import { TransactionsTableColumn, TransactionsTableSavedTemplateColumn } from '../../../shared/types/TransactionTableColumn';
import {
  Constants,
  CreateTypes,
  tableActions,
  transactionDirection,
  transactionTableTabs,
  transactionTypes
} from '../../../shared/utilities/Constants';
import { StandingAuthAchActions } from '../../../standing-authorizations/actions';
import { ViewPageActions } from '../../../transaction-view/actions';
import { selectInquiryCallComplete, selectIsEauthForEditFlow } from '../../../transaction-view/reducers';
import { selectAllTransactionsTab, selectPreferencesLoaded, selectRecentTab, selectRecurringTab, selectTemplatesTab, selectUpcomingTab } from '../../../user-preference/reducers/index';
import { AchActions, CheckActions, InternalTransferActions, PtrsActions, TransactionsTableActions, WireActions } from '../../actions';
import { editInProgress, gridReady } from '../../actions/transactions-table-actions';
import { CashieringTransaction, RecurringTransaction } from '../../models';
import { TransactionFormServiceError } from '../../models/form/TransactionForm';
import { DraftCommonWire } from '../../models/wire/WireTemplatesResponse';
import {
  selectCreateTransactionInProgress,
  selectCurrentTransaction,
  selectEditFlowNotInitiated,
  selectEditStartedTable,
  selectIsFromEdit,
  selectPtrsCallFinished,
  selectTemplateId,
  selectTransactionsTableCurrentTab,
  selectTransactionsTableState,
  selectTransactionTableDeleteTemplateError
} from '../../reducers';
import {
  amountColumnTemplate,
  currencyComparator,
  dateComparator,
  dateComparatorForFilter,
  defaultColumnTemplate,
  getDisplayFrequency,
  mapTransactionType,
  sharedColNames,
} from './table-helpers';
import { PreferenceTab } from '../../../user-preference/models/PreferenceTab';
import { defaultAllTabColumnDefs, defaultRecentTabColumnDefs, defaultRecurringTabColumnDefs, defaultUpcomingTabColumnDefs } from './transaction-table-definitions';
import { CoreService } from '@charlesschwab/sac-ui-core';

@Component({
  standalone: false,
  selector: 'mm-transactions-table',
  templateUrl: './transactions-table.component.html',
  styleUrls: ['./transactions-table.component.scss'],
})
export class TransactionsTableComponent implements OnInit {
  @Input() showCancelModalOnClick: boolean;

  private cancelLinkClicked$ = new BehaviorSubject<boolean>(false);
  cancelLinkClickedAsObservable$ = this.cancelLinkClicked$.asObservable();
  private transactionToCancel$ = new BehaviorSubject<TransactionsTableColumn>({
    Type: '',
    Direction: 'Outgoing',
    'To/From': '',
    'Transaction date': '',
    Amount: '',
    IsEditable: false,
    IsCancelable: false,
    IsActiveRecurring: false,
    isBundled: false
  });
  transactionToCancelAsObservable$ = this.transactionToCancel$.asObservable();
  private deleteClicked$ = new BehaviorSubject<boolean>(false);
  deleteLinkClickedAsObservable$ = this.deleteClicked$.asObservable();
  private deleteTemplateClicked$ = new BehaviorSubject<boolean>(false);
  deleteTemplateLinkClickedAsObservable$ = this.deleteTemplateClicked$.asObservable();
  private currentSelectedTransaction$ = new BehaviorSubject<string | null>(null);
  currentSelectedTransactionAsObservable$ = this.currentSelectedTransaction$.asObservable();
  private currentSelectedTemplate$ = new BehaviorSubject<string | null>(null);
  currentSelectedTemplateAsObservable$ = this.currentSelectedTemplate$.asObservable();
  scrollToSub: Subscription;
  focusPoint: string;
  fileName: string;
  noExportCol: string[] = ['Actions'];
  activeStoreTab: any = selectAllTransactions;
  isDemoUser: boolean;
  isEmployee: boolean;
  columnState: ColumnState[] | undefined;
  activeTabColumnDefs: ColDef[];
  colNames = {
    date: 'Transaction date',
    nextTransactionDate: 'Next transaction date',
    frequency: 'Frequency',
    amount: 'Amount',
    toFromAccount: sharedColNames.toFromAccount,
    transactionType: sharedColNames.transactionType,
    actions: sharedColNames.actions,
    status: 'Status',
    nickname: 'Nickname',
    recipientName: 'Recipient name',
    bankName: 'Bank name',
    accountNumber: 'Account number',
    dateCreated: 'Date created',
    IncomingOutgoing: 'Direction'
  }

  incomingOutgoingColumnObj = {
    aggFunc: null,
    colId: "Direction",
    floatingFilterComponent: 'customSetFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    flex: 1,
    hide: false,
    pinned: null,
    width: 120,
    pivot: false,
    pivotIndex: null,
    rowGroup: false,
    RowGroupindex: null,
    sort: null,
    sortIndex: null
  }
  nextTransactionColumnObj = {
    aggFunc: null,
    colId: "Next transaction date",
    floatingFilterComponent: 'customSetFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    flex: 1,
    hide: false,
    pinned: null,
    minWidth: 120,
    maxWidth: 120,
    pivot: false,
    pivotIndex: null,
    rowGroup: false,
    RowGroupindex: null,
    sort: null,
    sortIndex: null
  }

  statusColumnObj = {
    aggFunc: null,
    colId: "Status",
    flex: 1,
    hide: false,
    pinned: null,
    pivot: false,
    pivotIndex: null,
    rowGroup: false,
    rowGroupIndex: null,
    sort: null,
    sortIndex: null,
    width: 120
  }

  columnDefs: ColDef[] = [];
  defaultColumnDefs: ColDef[] = [
    {
      field: this.colNames.transactionType,
      sort: 'asc',
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      headerComponentParams: { template: amountColumnTemplate },
      flex: 1,
      minWidth: 270,
    },
    {
      field: this.colNames.IncomingOutgoing,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      headerComponentParams: { template: amountColumnTemplate },
      flex: 1,
      minWidth: 120
    },
    {
      field: this.colNames.toFromAccount,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      type: 'rightAligned',
      headerComponentParams: { template: amountColumnTemplate },
      flex: 1,
      minWidth: 360,
    },
    {
      field: this.colNames.amount,
      comparator: currencyComparator,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      type: 'rightAligned',
      headerComponentParams: { template: amountColumnTemplate },
      flex: 1,
      minWidth: 120,
    },
    {
      field: this.colNames.date,
      sort: null, //explicitly specify null to avoid sorting second column
      comparator: dateComparator,
      filter: agFilterNames.date,
      filterParams: {
        comparator: dateComparatorForFilter,
        inRangeInclusive: true,
        browserDatePicker: true
      },
      headerComponentParams: { template: amountColumnTemplate },
      minWidth: 160,
      maxWidth: 160
    },
    {
      field: this.colNames.frequency,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      headerComponentParams: { template: amountColumnTemplate },
      minWidth: 120,
      width: 120,
      maxWidth: 130
    },
    {
      field: this.colNames.status,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      headerComponentParams: { template: amountColumnTemplate },
      width: 120,
      minWidth: 120,
      maxWidth: 130
    },
    {
      field: this.colNames.actions,
      filter: false,
      suppressMovable: true,
      sortable: false,
      cellRenderer: ActionsLinkComponent,
      cellRendererParams: {
        onClick: this.actionLinkClicked.bind(this),
        viewStatus: 'View',
        edit: 'Edit',
        cancel: 'Cancel',
        deactivateProfile: 'Deactivate'
      },
      minWidth: 220,
      maxWidth: 220
    },
  ];
  sortByDateColumnDefs: ColDef[];

  templateActionsColumnDef = {
    field: this.colNames.actions,
    filter: false,
    suppressMovable: true,
    sortable: false,
    cellRenderer: ActionsLinkComponent,
    cellRendererParams: {
      onClick: this.actionLinkClicked.bind(this),
      startTransaction: 'Create transaction',
      viewStatus: 'View',
      deleteProfile: 'Delete',
      data: {
        isTemplate: true
      }
    },
    minWidth: 260,
    maxWidth: 260
  }

  transactionActionsColumnDef =   {
    field: this.colNames.actions,
    filter: false,
    suppressMovable: true,
    sortable: false,
    cellRenderer: ActionsLinkComponent,
    cellRendererParams: {
      onClick: this.actionLinkClicked.bind(this),
      viewStatus: 'View',
      edit: 'Edit',
      cancel: 'Cancel',
      deactivateProfile: 'Deactivate'
    },
    minWidth: 220,
    maxWidth: 220
  };

  templateColumnDefs: ColDef[] = [
    {
      field: this.colNames.nickname,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      cellRenderer: SloaNicknameComponent,
      flex: 1,
      minWidth: 120
    },
    {
      field: this.colNames.recipientName,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 120
    },
    {
      field: this.colNames.bankName,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      width: 180,
      minWidth: 120,
      maxWidth: 210
    },
    {
      field: this.colNames.accountNumber,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      minWidth: 140,
      maxWidth: 140
    },
    {
      field: this.colNames.dateCreated,
      comparator: dateComparator,
      filter: agFilterNames.date,
      filterParams: {
        comparator: dateComparatorForFilter,
        inRangeInclusive: true,
        browserDatePicker: true
      },
      minWidth: 160,
      maxWidth: 160
    },
    this.templateActionsColumnDef
  ];

  filterModel: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };
  rowData$: Observable<TransactionsTableColumn[] | TransactionsTableSavedTemplateColumn[]>;
  transactionsTableState$: Observable<TableComponentState>;
  accountInfoState: AccountInfoState;
  destroy$ = new Subject<void>();
  isPtrs: boolean;
  subAccountId: string;
  savePreferencesEnabled: boolean;
  isEauthForEditFlow: boolean | undefined;
  newEditEvent: any;
  currentRowTransaction: TransactionsTableColumn;
  createTransactionInProgress: boolean;
  hasNoTransactions: boolean;
  hasNoUpcomingTransactions: boolean;
  hasNoRecentTransactions: boolean;
  hasNoRecurringTransactions: boolean;
  hasNoSavedTemplates: boolean;
  editFlowNotInitiated: boolean;
  sortColumnDef: ColDef[] = [];
  currentTab: string;
  templateList: DraftCommonWire[];
  deleteTemplateCallError$: Observable<TransactionFormServiceError>;
  deleteTemplateCallFailed: boolean = false;
  templateIdInContext: string;
  hasHeaderStateError: boolean = false;
  currentActionLinkClickedEvent: any;
  currentPrefsTab: PreferenceTab;
  prefsLoaded: boolean = false;
  createTypeInContext: CreateTypes;

  constructor(
    private store: Store,
    private namePipe: NamePipe,
    private toastr: ToastrService,
    private coreService: CoreService
  ) { }


  ngOnInit(): void {
    this.store.select(selectHeaderStateServiceError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasHeaderStateError = error
    });

    this.activeTabColumnDefs = this.defaultColumnDefs;

    //clone and update columns
    this.sortByDateColumnDefs = JSON.parse(JSON.stringify(this.defaultColumnDefs));
    this.sortByDateColumnDefs.forEach(element => {
      if (element.field === this.colNames.transactionType) {
        element.sort = null;
      }

      else if (element.field === this.colNames.date) {
        element.sort = "asc"
      }
    });

    this.columnDefs = defaultAllTabColumnDefs.concat(this.transactionActionsColumnDef);
    this.store.select(selectPreferencesLoaded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectAllTransactionsTab)
        ),
        filter(([loaded]) => loaded === true)
      ).subscribe(([loaded, tab]) => {
        if (tab) {
          this.columnDefs = this.buildColumnPreferences(tab.columns);
        } else {
          this.columnDefs = defaultAllTabColumnDefs.concat(this.transactionActionsColumnDef);
        }
      });


    this.rowData$ = this.store.select(selectAllTransactions).pipe(map((cashieringTransactions) =>
      this.mapCashieringTransactionsToRowData(cashieringTransactions)
    ));

    this.store.select(selectHasNoUpcomingTransactions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoTransactions => {
        this.hasNoUpcomingTransactions = hasNoTransactions;
        this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
      });

    combineLatest([
      this.store.select(selectTransactionsTableCurrentTab),
      this.store.select(selectTemplateDeleteCallSuccessful),
      this.store.select(selectTemplateStartTransactionStarted),
      this.store.select(selectIsFromEdit)
    ])
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe(([currentTab, _deleteSuccessful, started, isFromEdit]) => {
        if (!isFromEdit) {
          this.currentTab = currentTab;
          if (this.currentTab === 'Saved wire templates' && !started) {
            this.store.dispatch(WireActions.callWireTemplatesGetListTable());
            this.whenTemplate();
          }
        }

      });

    this.store.select(selectHasNoTransactions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoTransactions => this.hasNoTransactions = hasNoTransactions);

    this.store.select(selectHasNoRecentTransactions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoTransactions => this.hasNoRecentTransactions = hasNoTransactions);

    this.store.select(selectHasNoRecurringTransactions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoTransactions => this.hasNoRecurringTransactions = hasNoTransactions);

    this.store.select(selectHasNoSavedTemplates).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoSavedTemplates => this.hasNoSavedTemplates = hasNoSavedTemplates)

    this.transactionsTableState$ = this.store.select(selectTransactionsTableState);

    if (this.currentTab === 'Saved wire templates') {
      this.fileName = `transactions-savedTemplates-`
    }
    else {
      this.fileName = `transactions-all-`;
    }

    this.setActiveTableEventListener();

    this.scrollToSub = this.store.select(selectPageScrollTo).subscribe(val => this.focusPoint = val);

    // Scroll to the account transactions table
    if (this.focusPoint === 'account-transactions-header') {
      this.scrollToAccountTransactions();
    }

    this.store.select(selectIsPtrs).pipe(
      takeUntil(this.destroy$)
    ).subscribe(isPtrs => this.isPtrs = isPtrs);

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

    this.store.select(selectIsEmployee).subscribe(value => {
      this.isEmployee = value;
    });

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => { this.subAccountId = subAccountId });

    this.store.select(selectCurrentTransaction).pipe(takeUntil(this.destroy$)).subscribe((_currentRowTransaction) => {
      this.currentRowTransaction = _currentRowTransaction as TransactionsTableColumn;
    });

    this.store
      .select(selectEditFlowNotInitiated)
      .pipe(takeUntil(this.destroy$))
      .subscribe((notInitiated) => (this.editFlowNotInitiated = notInitiated));

    combineLatest([
      this.store.select(selectPtrsCallFinished),
      this.store.select(selectInquiryCallComplete),
    ])
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsEauthForEditFlow),
          this.store.select(selectCurrentTransaction),
          this.store.select(selectEditStartedTable)
        ),
        filter(
          ([[_ptrsCallComplete, inquiryCallComplete], _isEauthForEditFlow, _currentTransaction, editStarted]) =>
            inquiryCallComplete && editStarted
        )
      )
      .subscribe(
        ([
          [isPtrsCallFinished, _inquiryCallComplete],
          isEauthForEditFlow,
          _currentTransaction,
        ]) => {
          if (isEauthForEditFlow === true) {
            this.store.dispatch(SharedActions.showEditingRestrictedModal());
            this.store.dispatch(ViewPageActions.resetIsEauthForEditFlow());
          } else if (isEauthForEditFlow === false) {
            //for PTRS accounts, don't call Edit until PTRS call finishes
            if ((this.isPtrs && isPtrsCallFinished) || !this.isPtrs) {
              this.store.dispatch(editInProgress());
              this.store.dispatch(ViewPageActions.resetIsEauthForEditFlow());
            }
          }
        }
      );

    this.store.select(selectCreateTransactionInProgress).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.createTransactionInProgress = val;
    });

    this.store.select(selectTemplatesList).pipe(takeUntil(this.destroy$)).subscribe(templateList => {
      this.templateList = templateList
    });

    this.store.select(selectCreateTypeInContext).pipe(takeUntil(this.destroy$)).subscribe(createType => {
      this.createTypeInContext = createType;
    });

    this.deleteTemplateCallError$ = this.store.select(selectTransactionTableDeleteTemplateError);
    this.deleteTemplateCallError$.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.deleteTemplateCallFailed = val.hasError;
    })

    this.store.select(selectTemplateId).pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.templateIdInContext = id ?? '';
    })

    this.store.select(selectPreferencesLoaded)
    .pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectAllTransactionsTab)
      )
    ).subscribe(([loaded, tab]) => {
      this.prefsLoaded = loaded;
      if (tab) {
        this.currentPrefsTab = tab;
      }
    })

    this.store.select(selectCancelModalContinueClickedTransactionsTablePage).
      pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
            this.store.select(selectCancelModalSource),
            this.store.select(selectTemplateId)
        )
      ).
      subscribe(_ => {
        if (this.currentActionLinkClickedEvent?.name === tableActions.viewStatus && !this.currentActionLinkClickedEvent.rowData?.isTemplate) {
          this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: this.currentActionLinkClickedEvent.rowData }));
          this.store.dispatch(TransactionsTableActions.loadViewPage({ viewType: 'transaction' }));
          this.callTransactionInquiry(this.currentActionLinkClickedEvent, true);
        }
        else if (this.currentActionLinkClickedEvent?.name === tableActions.edit) {
          this.newEditEvent = this.currentActionLinkClickedEvent;
          this.updateEditPage(true);
        } else if (this.currentActionLinkClickedEvent?.name === 'Create transaction') {
          const template = this.templateList.find(template => template.draftId === this.currentActionLinkClickedEvent?.rowData.DraftId);
          if (template && this.templateIdInContext !== template.draftId) {
            this.store.dispatch(
              TransactionsTableActions.startTransactionClicked({
                draftId: template.draftId,
              })
            );
          } else {
            this.scrollToTop();
          }
          this.store.dispatch(
            TransactionsTableActions.currentTabUpdated({ currentTab: 'All' })
          );
          this.activeStoreTab = selectAllTransactions;
          this.rowData$ = this.store.select(selectAllTransactions).pipe(map((cashieringTransactions) =>
            this.mapCashieringTransactionsToRowData(cashieringTransactions)
          ));
          if (this.currentPrefsTab && this.prefsLoaded) {
            this.columnDefs = this.buildColumnPreferences(this.currentPrefsTab.columns);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.currentTab = 'All';
        } else if (this.currentActionLinkClickedEvent?.name === tableActions.viewDetails) {
          this.store.dispatch(TransactionsTableActions.setCurrentViewedTemplate({ template: this.templateList.find(template => template.draftId === this.currentActionLinkClickedEvent?.rowData.DraftId) }));
          this.store.dispatch(TransactionsTableActions.loadViewPage({ viewType: 'template' }));
        }
      }
    )

    this.coreService.currentContextUpdated$
        .pipe(takeUntil(this.destroy$))
        .subscribe((context) => {

        this.activeTabColumnDefs = this.defaultColumnDefs;

        //clone and update columns
        this.sortByDateColumnDefs = JSON.parse(JSON.stringify(this.defaultColumnDefs));
        this.sortByDateColumnDefs.forEach(element => {
          if (element.field === this.colNames.transactionType) {
            element.sort = null;
          }

          else if (element.field === this.colNames.date) {
            element.sort = "asc"
          }
        });

        this.columnDefs = defaultAllTabColumnDefs.concat(this.transactionActionsColumnDef);
        this.store.select(selectPreferencesLoaded)
          .pipe(
            takeUntil(this.destroy$),
            withLatestFrom(
              this.store.select(selectAllTransactionsTab)
            ),
            filter(([loaded]) => loaded === true)
          ).subscribe(([loaded, tab]) => {
            if (tab) {
              this.columnDefs = this.buildColumnPreferences(tab.columns);
            } else {
              this.columnDefs = defaultAllTabColumnDefs.concat(this.transactionActionsColumnDef);
            }
          });

        this.rowData$ = this.store.select(selectAllTransactions).pipe(map((cashieringTransactions) =>
          this.mapCashieringTransactionsToRowData(cashieringTransactions)
        ));

        this.transactionsTableState$ = this.store.select(selectTransactionsTableState);

        if (this.currentTab === 'Saved wire templates') {
          this.fileName = `transactions-savedTemplates-`
        }
        else {
          this.fileName = `transactions-all-`;
        }

        this.setActiveTableEventListener();

        this.store.select(selectPreferencesLoaded)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(
            this.store.select(selectAllTransactionsTab)
          )
        ).subscribe(([loaded, tab]) => {
          this.prefsLoaded = loaded;
          if (tab) {
            this.currentPrefsTab = tab;
          }
        })

        this.scrollToTop();
        this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: 'All' }));
        this.setActiveTableEventListener();
    });
  }

  ngOnDestroy(): void {
    this.scrollToSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  mapCashieringTransactionsToRowData(
    transactions: (CashieringTransaction | RecurringTransaction)[]
  ): TransactionsTableColumn[] {
    return transactions.map((transaction) => {
      return this.instanceOfCashiering(transaction)
        ? this.mapCashieringTransactions(transaction)
        : this.mapRecurringTransactions(transaction);
    });
  }

  mapSavedTemplatesToRowData(
    templates: (DraftCommonWire)[]
  ): TransactionsTableSavedTemplateColumn[] {
    return templates.map((template) => {
      return this.mapTemplates(template);
    });
  }

  instanceOfCashiering(transaction: CashieringTransaction | RecurringTransaction): transaction is CashieringTransaction {
    return 'fundsDirection' in transaction;
  }

  actionLinkClicked(event: any) {
    // TO-DO for future actions
    if (event.name === 'View') {
      if(event.rowData?.isTemplate) {
        this.viewTemplateClicked(event);
      } else {
        this.statusClicked(event);
      }
    } else if (event.name === 'Edit') {
      this.editClicked(event);
    } else if (event.name === 'Cancel') {
      this.cancelClicked(event);
    } else if (event.name === 'Deactivate') {
      this.deleteClicked(event);
    } else if (event.name === 'Delete') {
      this.deleteTemplateClicked(event);
    } else if (event.name === 'View') {
      this.viewTemplateClicked(event);
    } else if (event.name === 'Create transaction') {
      this.startTransactClicked(event)
    }
  }

  startTransactClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableStartTransaction'
      }));
    } else {
      const template = this.templateList.find(template => template.draftId === event.rowData?.DraftId);
      if (template && this.templateIdInContext !== template.draftId) {
        this.store.dispatch(
          TransactionsTableActions.startTransactionClicked({
            draftId: template.draftId,
          })
        );
      } else {
        this.scrollToTop();
      }
      this.store.dispatch(
        TransactionsTableActions.currentTabUpdated({ currentTab: 'All' })
      );
      this.activeStoreTab = selectAllTransactions;
      this.rowData$ = this.store.select(selectAllTransactions).pipe(map((cashieringTransactions) =>
        this.mapCashieringTransactionsToRowData(cashieringTransactions)
      ));
      this.store.select(selectPreferencesLoaded)
        .pipe(
          takeUntil(this.destroy$),
          withLatestFrom(
            this.store.select(selectAllTransactionsTab)
          ),
        ).subscribe(([loaded, tab]) => {
          if (tab && loaded) {
            this.columnDefs = this.buildColumnPreferences(tab.columns);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
        });
      this.currentTab = 'All';
    }
  }

  statusClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableViewStatus'
      }));
    } else {
      const rowData = event.rowData as TransactionsTableColumn;
        this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
        this.store.dispatch(TransactionsTableActions.loadViewPage({ viewType: 'transaction' }));
        if (this.isEditRecurringAch(rowData)) {
          this.store.dispatch(SharedActions.updatePreviousContext({createType: this.createTypeInContext}));
        }
        this.callTransactionInquiry(event, true);
    }
  }

  editClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableEdit'
      }));
    } else {
      this.newEditEvent = event;
      this.updateEditPage(true);
    }
  }

  cancelClicked(event: any) {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else {
      const rowData = event.rowData as TransactionsTableColumn;
      this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
      this.cancelLinkClicked$.next(true);
    }
  }

  deleteClicked(event: any) {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else {
      const rowData = event.rowData;
      this.currentSelectedTransaction$.next(rowData.Type[0] + '-' + rowData.CashieringRqstId + '-' + rowData.CashieringRqstSecondId)
      this.deleteClicked$.next(true);
    }
  }

  deleteTemplateClicked(event: any) {
    const rowData = event.rowData as TransactionsTableSavedTemplateColumn;
    this.store.dispatch(TransactionsTableActions.setCurrentViewedTemplate({ template: this.templateList.find(template => template.draftId === rowData.DraftId) }));
    this.currentSelectedTemplate$.next(rowData.DraftId);
    this.deleteTemplateClicked$.next(true);
  }

  viewTemplateClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableView'
      }));
    } else {
      const rowData = event.rowData;
      this.store.dispatch(TransactionsTableActions.setCurrentViewedTemplate({ template: this.templateList.find(template => template.draftId === rowData.DraftId) }));
      this.store.dispatch(TransactionsTableActions.loadViewPage({ viewType: 'template' }));
    }
  }

  callTransactionInquiry(event: any, isFromView: boolean) {
    this.store.pipe(takeUntil(this.destroy$),
      select(this.activeStoreTab),
      map((element: any) => element.find((transaction: any) => { return transaction.cashieringRqstId === event.rowData['CashieringRqstId'] })),
      filter(res => res !== undefined)
    ).subscribe((res) => {
      if (res.typeDescription?.includes('Journal')) {
        this.store.dispatch(InternalTransferActions.callInquiry({
          payload: res,
          viewType: 'transaction',
          transactionType: transactionTypes.internalTransfer
        }));
        if (this.isPtrs) {
          this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
        }
      } else if (res.typeDescription?.includes('MoneyLink')) {
        if (res.frequency !== " " && res.frequency !== "On-Request" && event.name === 'Edit') {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.ach,
            transferDirection: (res.incomingAmount != null) ? 'incoming' : 'outgoing',
            standingAuthId: `a-${res.cashieringRqstId}-${res.cashieringRqstSecondId}`,
            frequency: 'recurring'
          };
          this.store.dispatch(
            TransactionsTableActions.editProfileClicked({ rowData })
          );
          this.store.dispatch(StandingAuthAchActions.callAchInquiry({
            payload: res,
            viewType: 'transaction',
            transactionType: transactionTypes.ach
          }));
          if (this.isPtrs) {
            this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
          }
        } else {
          this.store.dispatch(AchActions.callInquiry({
            payload: res,
            viewType: 'transaction',
            transactionType: transactionTypes.ach
          }))
        }
      } else if (res.typeDescription?.includes('Wire')) {
        this.store.dispatch(WireActions.callInquiry({
          payload: res,
          viewType: 'transaction',
          transactionType: transactionTypes.wire,
          resourceVersion: '3'
        }));
      } else if (res.typeDescription?.includes('Check')) {
        this.store.dispatch(CheckActions.callInquiry({
          payload: res,
          viewType: isFromView ? 'transaction' : '',
          transactionType: transactionTypes.check
        }));
        if (this.isPtrs && (event.name === 'Edit' || isFromView)) {
          this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
        }
      }
    })
  }

  mapTransactions(
    transaction: CashieringTransaction | RecurringTransaction,
    isActiveRecurring: boolean
  ): TransactionsTableColumn {
    let mappedTransaction: TransactionsTableColumn;
    mappedTransaction = {
      Type: mapTransactionType(
        transaction.typeDescription,
        this.findTransactionDirection(transaction)
      ),
      Direction: this.findTransactionDirection(transaction),
      'To/From': this.namePipe.transform(
        `${transaction.to1} ${transaction.to2} ${transaction.to3}`
      ),
      Amount: transaction.outgoingAmount
        ? (transaction.outgoingAmount as string)
        : (transaction.incomingAmount as string),
      Status: transaction.statusCodeDescription,
      CashieringRqstId: transaction.cashieringRqstId,
      CashieringRqstSecondId: transaction.cashieringRqstSecondId,
      'IsEditable': transaction.isEditable,
      'IsCancelable': transaction.isCancelable,
      'IsActiveRecurring': isActiveRecurring,
      isBundled: transaction.bundledSloaIn
    };

    if ( isActiveRecurring ) {
      mappedTransaction = {
        ...mappedTransaction,
        'Next transaction date': transaction.processDate.split('-').join('/'),
      };
    }
    else {
      mappedTransaction = {
        ...mappedTransaction,
        'Transaction date': transaction.processDate.split('-').join('/')
      }
    }
    return  mappedTransaction;
  }

  findTransactionDirection(transaction: CashieringTransaction | RecurringTransaction){
    return transaction.incomingAmount === null || transaction.incomingAmount === undefined ? transactionDirection.out : transactionDirection.in
  }

  mapTemplates(
    template: DraftCommonWire
  ): TransactionsTableSavedTemplateColumn {
    const splitDate: string[] = template.createDate.slice(0, 10).split('-');
    const year: string = splitDate[0];
    const month: string = splitDate[1];
    const day: string = splitDate[2];
    const orderedDate: string[] = [month, day, year];
    const isIsoCompliant = !!template.draftObject.isIsoCompliant;
    return {
      'Nickname': template.draftObject.nickname,
      'Recipient name': isIsoCompliant ? template.draftObject?.beneficiary?.name ?? '' : this.namePipe.transform(
        `${template.draftObject.beneficiary.namePartOne} ${template.draftObject.beneficiary.namePartTwo}`
      ),
      'Bank name': this.namePipe.transform(
        `${template.draftObject.beneficiaryBank.namePartOne} ${template.draftObject.beneficiaryBank.namePartTwo}`
      ),
      'Account number': template.subAccount,
      'Date created': orderedDate.join('/'),
      DraftId: template.draftId,
      hasRequestTransactionAccess:
          !this.hasHeaderStateError
    };
  }

  mapCashieringTransactions(
    transaction: CashieringTransaction
  ): TransactionsTableColumn {
    return {
      ...this.mapTransactions(transaction, false),
      Type: mapTransactionType(
        transaction.typeDescription,
        transaction.fundsDirection
      ),
      Direction: transaction.fundsDirection,
      Frequency: getDisplayFrequency(transaction.frequencyCode, transaction.mediaTypeCd, transaction.isRecurring)
    };
  }

  mapRecurringTransactions(transaction: RecurringTransaction): TransactionsTableColumn {
    return {
      ...this.mapTransactions(transaction, true),
      Frequency: transaction.frequencyCd ? getDisplayFrequency(transaction.frequencyCd, transaction.mediaTypeCd) : transaction.frequencyDescription
    };
  }

  mapSavedTemplates(
    template: DraftCommonWire
  ): TransactionsTableSavedTemplateColumn {
    return {
      ...this.mapTemplates(template)
    };
  }

  updateGridReady(ready: boolean) {
    this.store.dispatch(gridReady({ gridReady: ready, tab: this.currentTab === 'Saved wire templates' ? 'Saved wire templates' : 'All' }));
  }

  updateEditPage(accepted: boolean) {
    if (accepted) {
      this.store.dispatch(TransactionsTableActions.editTransactionClicked());
      const rowData = this.newEditEvent.rowData;
      if (this.isEditRecurringAch(rowData)) {
        this.store.dispatch(SharedActions.updatePreviousContext({createType: this.createTypeInContext}));
      }
      this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
      this.callTransactionInquiry(this.newEditEvent, false);
      this.scrollToTop();
    }
  }

  private isEditRecurringAch(rowData: any): boolean {
    let frequency: string = rowData.Frequency;
    let type: string = rowData.Type;

    if (!type.includes('ACH')) {
      return false;
    }
    if (frequency.length === 0 || frequency === 'One-time') {
      return false
    }
    return true;
  }

  private setActiveTableEventListener(): void {
    const activeTable = document.getElementById('transaction-table-tabs');
    activeTable?.addEventListener(
      'sdpsSelect',
      ((event: Event) => {
        // sdpsSelect is a custom Event from Everest
        // e.detail contains Id of sdpsSelct
        const e = event as CustomEvent;
        //This is to pass fileName for export functionality
        this.fileName = `transactions-${e.detail}-`;
        switch (e.detail as string) {
          case 'all':
            this.rowData$ = this.store
              .select(selectAllTransactions)
              .pipe(map((transactions) => this.mapCashieringTransactionsToRowData(transactions)));
              if (this.hasNoRecentTransactions) {
                this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
              }
              var hasIncomingOutgoing = false;
              this.store.select(selectAllTransactionsTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
                tab?.columns.map(sobj => {
                  if(sobj?.colId?.includes('Direction')) {
                    hasIncomingOutgoing = true;
                  }
                });
                this.columnState = tab?.columns;
                if(this.columnState && !hasIncomingOutgoing) {
                  this.columnState = Object.assign([], this.columnState);
                  this.columnState?.splice(1, 0, this.incomingOutgoingColumnObj);
                }
              });
              if (this.columnState) {
                this.columnDefs = this.buildColumnPreferences(this.columnState);
              } else {
                this.columnDefs = defaultAllTabColumnDefs.concat(this.transactionActionsColumnDef);
              }
              this.activeStoreTab = selectAllTransactions
              this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: transactionTableTabs.all }));
              this.activeTabColumnDefs = this.defaultColumnDefs;

              break;
          case 'upcoming':
            this.rowData$ = this.store
              .select(selectUpcomingTransactions)
              .pipe(map((transactions) => this.mapCashieringTransactionsToRowData(transactions)));
            if (this.hasNoUpcomingTransactions) {
              this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
            }
            var hasStatusColumn = false;
            var hasIncomingOutgoing = false;
            this.store.select(selectUpcomingTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              tab?.columns.map(sobj => {
                if(sobj?.colId?.includes('Direction')) {
                  hasIncomingOutgoing = true;
                }
                if(sobj?.colId?.includes('Status')) {
                  hasStatusColumn = true;
                }
              });
              this.columnState = tab?.columns;
              if(this.columnState && !hasIncomingOutgoing) {
                this.columnState = Object.assign([], this.columnState);
                this.columnState?.splice(1, 0, this.incomingOutgoingColumnObj);
              }
              if(this.columnState && !hasStatusColumn) {
                this.columnState = Object.assign([], this.columnState);
                this.columnState?.splice(this.columnState.length-1, 0, this.statusColumnObj);
              }
            });
            if (this.columnState) {
              this.columnDefs = this.buildColumnPreferences(this.columnState);
            } else {
              this.columnDefs = defaultUpcomingTabColumnDefs.concat(this.transactionActionsColumnDef);
            }
            this.activeStoreTab = selectUpcomingTransactions
            this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: transactionTableTabs.upcoming }));
            this.activeTabColumnDefs = this.defaultColumnDefs;

            break;
          case 'recent':
            this.rowData$ = this.store
              .select(selectRecentTransactions)
              .pipe(map((transactions) => this.mapCashieringTransactionsToRowData(transactions)));
            if (this.hasNoRecentTransactions) {
              this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
            }
            var hasIncomingOutgoing = false;
            this.store.select(selectRecentTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              tab?.columns.map(sobj => {
                if(sobj?.colId?.includes('Direction')) {
                  hasIncomingOutgoing = true;
                }
              });
              this.columnState = tab?.columns;
              if(this.columnState && !hasIncomingOutgoing) {
                this.columnState = Object.assign([], this.columnState);
                this.columnState?.splice(1, 0, this.incomingOutgoingColumnObj);
              }
            });
            if (this.columnState) {
              this.columnDefs = this.buildColumnPreferences(this.columnState);
            } else {
              this.columnDefs = defaultRecentTabColumnDefs.concat(this.transactionActionsColumnDef);
            }
            this.activeStoreTab = selectRecentTransactions
            this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: transactionTableTabs.recent }));
            this.activeTabColumnDefs = this.defaultColumnDefs;

            break;
          case 'recurring':
            this.rowData$ = this.store
              .select(selectRecurringTransactions)
              .pipe(map((transactions) => this.mapCashieringTransactionsToRowData(transactions)));
            if (this.hasNoRecurringTransactions) {
              this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
            }
            var hasIncomingOutgoing = false;
            var hasTransactionDate = false;
            this.store.select(selectRecurringTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
              tab?.columns.map(sobj => {
                if(sobj?.colId?.includes('Direction')) {
                  hasIncomingOutgoing = true;
                }
                if(sobj?.colId?.includes('Transaction date')){
                  hasTransactionDate = true;
                }
              });
              this.columnState = tab?.columns;
              if(this.columnState && !hasIncomingOutgoing) {
                this.columnState = Object.assign([], this.columnState);
                this.columnState?.splice(1, 0, this.incomingOutgoingColumnObj);
              }
              if(this.columnState && hasTransactionDate){
                this.columnState = Object.assign([], this.columnState);
                this.columnState?.splice(4, 1, this.nextTransactionColumnObj);

              }
            });
            if (this.columnState) {
              this.columnDefs = this.buildColumnPreferences(this.columnState);
            } else {
              this.columnDefs = defaultRecurringTabColumnDefs.concat(this.transactionActionsColumnDef);
            }
            this.activeStoreTab = selectRecurringTransactions
            this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: transactionTableTabs.recurring }));
            this.activeTabColumnDefs = defaultRecurringTabColumnDefs.concat(this.transactionActionsColumnDef);;
            break;
          case 'savedTemplates':
            this.whenTemplate();
            this.activeTabColumnDefs = this.templateColumnDefs;

            break;
          default:
            this.columnDefs = this.defaultColumnDefs;
        }
      }
      ));
  }

  whenTemplate() {
    this.rowData$ = this.store
      .select(selectTemplatesList)
      .pipe(map((transactions) => this.mapSavedTemplatesToRowData(transactions)));

    this.store.select(selectTemplatesTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
      this.columnState = tab?.columns;
    });
    if (this.columnState) {
      this.columnDefs = this.buildColumnPreferences(this.columnState, true);
    } else {
      this.columnDefs = this.templateColumnDefs;
    }
    if (this.hasNoSavedTemplates) {
      this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
    }

    this.store.select(selectTemplatesTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
      this.columnState = tab?.columns;
    });

    this.activeStoreTab = selectTemplatesList;
    this.store.dispatch(TransactionsTableActions.currentTabUpdated({ currentTab: transactionTableTabs.templates }));
  }

  buildColumnPreferences(preferences: ColumnState[], isSavedTemplateTab: boolean = false): ColDef[] {
    // Remove previously existing columns that will now appear empty and prevent new bad columns
    let updatedPreferences: ColumnState[] = [];
    var hasDirection = false;
    preferences.forEach(preference => {
      if(
      !preference.colId?.includes('Funds direction') &&
      !preference.colId?.includes('Incoming\/Outgoing') &&
      isNaN(Number(preference.colId))){
        updatedPreferences.push(preference);
      }
      if (preference.colId?.includes('Direction')) {
        hasDirection = true;
      }
    });

    if (!hasDirection && !isSavedTemplateTab) {
      updatedPreferences.splice(1, 0, this.incomingOutgoingColumnObj);
    }

    let result: ColDef[] = updatedPreferences.map((preference) => {
      switch (preference.colId) {
        case 'Type':
          return {
            field: this.colNames.transactionType,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 270,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Direction':
          return {
            field: this.colNames.IncomingOutgoing,
            sort: preference.sort,
            flex: 1,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filter: agFilterNames.set,
            minWidth: 120,
            hide: false,
            pinned: null,
            pivot: false,
            pivotIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            sortIndex: null,
            aggFunc: null,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'To/From':
          return {
            field: this.colNames.toFromAccount,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll('-', '')
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 360,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Amount':
          return {
            field: this.colNames.amount,
            comparator: currencyComparator,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll(',', '')
            },
            type: 'rightAligned',
            headerComponentParams: { template: amountColumnTemplate },
            minWidth: 120,
            width: 120,
            maxWidth: 140,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Transaction date':
          return {
            field: this.colNames.date,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

          case 'Next transaction date':
          return {
            field: this.colNames.nextTransactionDate,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 170,
            maxWidth: 180,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Frequency':
          return {
            field: this.colNames.frequency,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 120,
            minWidth: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Actions':
          return isSavedTemplateTab
            ? this.templateActionsColumnDef
            : {
              field: this.colNames.actions,
              filter: false,
              suppressMovable: true,
              sortable: false,
              cellRenderer: ActionsLinkComponent,
              cellRendererParams: {
                onClick: this.actionLinkClicked.bind(this),
                viewStatus: 'View',
                edit: 'Edit',
                cancel: 'Cancel',
                deactivateProfile: 'Deactivate',
              },
              minWidth: 220,
              maxWidth: 220,
            };
        case 'Status':
          return {
            field: this.colNames.status,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 120,
            minWidth: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Nickname':
          return {
            field: this.colNames.nickname,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 120,
            cellRenderer: SloaNicknameComponent,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Recipient name':
          return {
            field: this.colNames.recipientName,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Bank name':
          return {
            field: this.colNames.bankName,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 180,
            minWidth: 120,
            maxWidth: 210,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Account number':
          return {
            field: this.colNames.accountNumber,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 140,
            maxWidth: 140,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Date created':
          return {
            field: this.colNames.dateCreated,
            sort: preference.sort,
            comparator: dateComparator,
            filter: agFilterNames.date,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        default:
          return {

          }
      }
    })
    return result;
  }

  scrollToAccountTransactions(): void {
    const elem = document.getElementById('account-transactions-header');
    setTimeout(() => {
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    }, 0)
  }


  scrollToTop(): void {
    const elem = document.getElementById('transaction-form-header');
    setTimeout(() => {
      elem?.focus();
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    });
  }
}
